<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2"
                 v-model="selectedValue">
        <el-option key="quickView"
                   :label="$t('views.common.listView.quickView')"
                   value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')"
                   value="allItems">
        </el-option>
        <el-option
            v-if="$can('delete', '/examination/zeiss-cards')"
            key="deletedItems"
            :label="$t('views.common.listView.deletedItems')"
            value="deletedItems"
        >
        </el-option>
        <el-option
            key="export"
            :label="$t('views.common.listView.selectedRows.export')"
            value="export"
        >
        </el-option>
        <el-option
            v-if="selectedValue != 'deletedItems' && $can('delete', '/examination/zeiss-cards')"
            key="delete"
            :label="$t('views.common.listView.selectedRows.delete')"
            value="delete"
        >
        </el-option>
        <el-option
            v-if="selectedValue == 'deletedItems' && $can('delete', '/examination/zeiss-cards')"
            key="restore"
            :label="$t('views.common.listView.selectedRows.restore')"
            value="restore"
        >
        </el-option>
      </el-select>

      <el-input
          :placeholder="$t('views.common.listView.searchRows')"
          class="w-full col-span-12 md:col-span-5"
          :prefix-icon="Search"
          v-model="searchTerm"
      />

      <div class="col-span-12 md:col-span-5 flex items-center gap-4">
        <horizontal-button
            :title="$t('views.examinations.zeissCardsPage.settings')"
            faIcon="cogs"
            v-if="$can('update', '/examination/zeiss-cards')"
            @click.prevent="$router.push($Route.EXAMINATIONS_ZEISS_CARDS_SETTINGS)"
        />
        <horizontal-button
            :title="$t('views.examinations.zeissCardsPage.newCard')"
            v-if="$can('create', '/examination/zeiss-cards')"
            @click.prevent="$router.push($Route.EXAMINATIONS_ZEISS_CARDS_NEW_CARD)"
        />
      </div>
    </div>

    <ag-grid-vue
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 85vh"
        :rowData="rowData"
        :defaultColDef="defaultColDef"
        :pagination="selectedValue == 'allItems'"
        :paginationAutoPageSize="selectedValue == 'allItems'"
        :onGridReady="onGridReady"
        @cell-double-clicked="onCellDoubleClicked"
    >
    </ag-grid-vue>

    <div class="mt-2">{{ $t('views.common.listView.count') }}: {{ rowData.length }}</div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {AgGridVue} from 'ag-grid-vue3'
import {CellDoubleClickedEvent, ColDef, GridApi, GridReadyEvent} from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'
import {Search} from '@element-plus/icons-vue'
import {useI18n} from 'vue-i18n'
import {useStorage} from '@vueuse/core'
import {formatPostgresDate} from '@/utils/date'
import ZeissCard, {zeissCardI18nMessages} from '@/models/customer/ZeissCard'
import ZeissCardCellRenderer
  from '@/views/examinations/zeissCards/ZeissCardCellRenderer.vue'

export default defineComponent({
  name: 'zeiss-cards',

  components: {
    AgGridVue,
    HorizontalButton
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {...zeissCardI18nMessages.en},
        ar: {...zeissCardI18nMessages.ar}
      }
    })

    return {t, Search}
  },

  data() {
    return {
      columnDefs: [] as ColDef[],
      rowData: [] as ZeissCard[],
      defaultColDef: {} as ColDef,
      gridApi: undefined as GridApi | undefined,
      searchTerm: useStorage('zeiss-cards-search-term', ''),
      selectedValue: 'quickView',
      showingDeleted: false
    }
  },

  beforeMount() {
    this.defaultColDef = {
      sortable: true,
      filter: false,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        headerName: `${this.t('system')} #`,
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true
      },
      {
        headerName: this.t('customerName'),
        field: 'customerName'
      },
      {
        headerName: this.t('orderNumber'),
        field: 'orderNumber',
        cellStyle: {textAlign: 'center'}
      },
      {
        headerName: this.t('models.common.date'),
        field: 'createdAt',
        cellStyle: {textAlign: 'center'},
        valueGetter: (params) => {
          const card = params.data as ZeissCard | undefined
          return card?.createdAt
              ? formatPostgresDate(String(card.createdAt), 'DD/MM/YYYY')
              : ''
        }
      },
      {
        headerName: this.t('models.common.time'),
        field: 'createdAt',
        cellStyle: {direction: 'ltr !important', textAlign: 'center'},
        valueGetter: (params) => {
          const card = params.data as ZeissCard | undefined
          return card?.createdAt
              ? formatPostgresDate(String(card.createdAt), 'hh:mm A')
              : ''
        }
      },
      {
        headerName: this.t('practitioner'),
        field: 'practitioner',
        valueGetter: (params) => {
          const card = params.data as ZeissCard | undefined
          return card?.practitioner ?? ''
        }
      },
      {
        cellRenderer: ZeissCardCellRenderer,
        cellRendererParams: {
          onPrintBtnClicked: this.onPrintBtnClicked
        }
      }
    ]
  },

  async mounted() {
    await this.fetchCards()
  },

  watch: {
    searchTerm() {
      this.gridApi?.setQuickFilter(this.searchTerm)
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export' || oldValue == 'restore') {
        return
      }

      if (newValue === 'delete' || newValue === 'export' || newValue === 'restore') {
        const selected = this.gridApi?.getSelectedRows() as ZeissCard[] | undefined
        if (selected?.length) {
          if (newValue === 'delete') {
            await this.deleteCards(selected)
            await this.fetchCards()
          } else if (newValue === 'restore') {
            await this.restoreCards(selected)
            await this.fetchCards()
          } else {
            this.gridApi?.exportDataAsCsv({onlySelected: true})
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }
        }
        this.selectedValue = oldValue
      } else {
        const deleted = newValue === 'deletedItems'
        if (deleted !== this.showingDeleted) {
          this.showingDeleted = deleted
          await this.fetchCards()
        }
      }
    }
  },

  methods: {
    onGridReady(params: GridReadyEvent) {
      this.gridApi = params.api
      this.gridApi.sizeColumnsToFit()
      if (this.searchTerm) {
        this.gridApi.setQuickFilter(this.searchTerm)
      }
    },
    async fetchCards() {
      try {
        const resp = await this.$http.get<ZeissCard[]>(ZeissCard.ENDPOINT, {
          params: {deleted: this.showingDeleted}
        })
        this.rowData = resp.map((card) => ZeissCard.from(card))
      } catch (err) {
        const error = err as {title: string; body: string}
        this.$alertModal.showDanger({title: error.title, body: error.body})
      }
    },
    async deleteCards(cards: ZeissCard[]) {
      if (!confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        return
      }
      try {
        await this.$http.delete(ZeissCard.ENDPOINT, {data: cards})
        this.$alertModal.showSuccess({
          title: this.$t('views.common.listView.selectedRows.deleteSuccess')
        })
      } catch (err) {
        const error = err as {title: string; body: string}
        this.$alertModal.showDanger({title: error.title, body: error.body})
      }
    },
    async restoreCards(cards: ZeissCard[]) {
      if (!confirm(this.$t('views.common.listView.selectedRows.restoreConfirm'))) {
        return
      }
      try {
        await this.$http.post('/helpers/restore-deleted', {
          data: cards,
          table: 'zeiss_cards'
        })
        this.$alertModal.showSuccess({
          title: this.$t('views.common.listView.selectedRows.restoreSuccess')
        })
      } catch (err) {
        const error = err as {title: string; body: string}
        this.$alertModal.showDanger({title: error.title, body: error.body})
      }
    },
    onCellDoubleClicked(event: CellDoubleClickedEvent) {
      const card = event.data as ZeissCard | undefined
      if (!card?.id || this.showingDeleted || !this.$can('update', '/examination/zeiss-cards')) {
        return
      }
      this.$router.push({
        path: this.$Route.EXAMINATIONS_ZEISS_CARDS_CARD_EDIT.replace(':card_id', card.id),
        state: ZeissCard.toHistoryState(card)
      })
    },
    onPrintBtnClicked(card: ZeissCard) {
      this.$router.push({
        path: this.$Route.EXAMINATIONS_ZEISS_CARDS_CARD_PRINT.replace(':card_id', card.id ?? ''),
        state: ZeissCard.toHistoryState(card)
      })
    }
  }
})
</script>

<style></style>
